import React, { useState, useEffect, useContext, useMemo, MouseEvent } from 'react';
import { AlertService, Timing, unitPriceCalculation } from '@spoiler-alert/ui-library';
import { type InternalRefetchQueriesInclude, useMutation } from '@apollo/client';
import { negotiationOrigin } from '../../../enums';
import {
  ActiveOfferShiftsQuery,
  AwardSummaryQuery,
  OfferListingsByInventoryQuery,
  StagedNegotiationSummaryQuery,
  trucklanePollingQuery,
} from '../../../graphql/queries';
import {
  AwardOfferListing,
  UnawardOfferListing,
  UnawardAndNegotiateOfferListing,
  createNegotiationStagedListings,
  IgnoreOfferListings,
  RestoreOfferListings,
  UpdateBidOnAwardedOfferListing,
} from '../../../graphql/mutations';
import deleteCachedFieldsOnUserQuery from '../../../apollo/cache-helpers/delete-cached-fields-on-user-query';
import destinationType from '../../../enums/staged-inventory-destination-type';
import deleteCachedFieldsOnOfferReviewPollingQuery from '../../../apollo/cache-helpers/delete-cached-fields-on-offer-review-polling-query';
import { TrucklaneActionInProgressContext } from '../../trucklane/trucklane-utility';
import Tracking from '../../../utilities/tracking-apis';
import OfferCardLayout from './offer-card-layout';
import { type OfferCardOfferListing, type OfferCardUser, type OfferCardOfferShift, OFFER_SHIFT_STATUS } from './offer-card-types';
import OfferShiftContext from '../offer-shift-context';
import type { Inventory } from '../../../graphql/query-result-types';
import { POSSIBLE_ACTIONS } from './offer-card-enums';

interface OfferCardProps {
  disabled: boolean;
  setOfferCardsEnabledState: (enabled: boolean) => void;
  inventoryId: string;
  offerInfo: OfferCardOfferListing;
  inventory: Inventory;
  user: OfferCardUser;
  fullstoryTag?: string;
  showSuggestions?: boolean;
  showNegotiationSuggestions?: boolean;
  additionalRefetchQueries?: InternalRefetchQueriesInclude;
  styles?: {
    offerCardWrapper: string;
  };
}

const OfferCardV3: React.FC<OfferCardProps> = ({
  disabled,
  setOfferCardsEnabledState,
  offerInfo,
  inventory,
  user,
  fullstoryTag,
  showSuggestions = false,
  showNegotiationSuggestions = false,
  additionalRefetchQueries,
  styles = {},
}) => {
  const [quantity, setQuantity] = useState(0);
  const [unitPrice, setUnitPrice] = useState(0);
  const [showWarningModal, setShowWarningModal] = useState(false);

  const refetchQueries = (negotiation = false) => {
    const queries: InternalRefetchQueriesInclude = [
      {
        query: AwardSummaryQuery,
      },
      {
        query: OfferListingsByInventoryQuery,
        variables: { inventoryIds: [inventory._id] },
      },
    ];
    if (negotiation) {
      queries.push({
        query: StagedNegotiationSummaryQuery,
      });
    }
    if (showSuggestions) {
      queries.push({
        query: trucklanePollingQuery,
        variables: { pollingDetails: { origin: inventory.siteId, truckType: inventory.truckType } },
      });
    }
    if (offerInfo.originalOfferListingId) {
      queries.push({
        query: ActiveOfferShiftsQuery,
      });
    }
    if (additionalRefetchQueries) {
      queries.push(...additionalRefetchQueries);
    }
    return queries;
  };

  const [unawardOfferListing, { loading: unawardLoading }] = useMutation(UnawardOfferListing);
  const [awardOfferListing, { loading: awardLoading }] = useMutation(AwardOfferListing);
  const [ignoreOfferListing, { loading: ignoreLoading }] = useMutation(IgnoreOfferListings);
  const [restoreOfferListing, { loading: restoreLoading }] = useMutation(RestoreOfferListings);
  const [negotiateOfferListing, { loading: negotiateLoading }] = useMutation(createNegotiationStagedListings);
  const [unawardAndNegotiateOfferListing, { loading: unawardAndNegotiateLoading }] = useMutation(UnawardAndNegotiateOfferListing);
  const [updateBidOnAwardedOfferListing, { loading: updateBidOnAwardedOfferListingLoading }] = useMutation(UpdateBidOnAwardedOfferListing);
  const { setActionInProgress } = useContext(TrucklaneActionInProgressContext);
  const offerShiftContextValue = useContext(OfferShiftContext);
  const isAwarded = offerInfo.status === 'AWARDED';

  useEffect(() => {
    setQuantity(isAwarded ? offerInfo.quantity : Math.min(offerInfo.quantity, inventory.availableQuantity));
    setUnitPrice(unitPriceCalculation(offerInfo.totalPrice, offerInfo.quantity, offerInfo.pricingDivisor));
  }, [offerInfo.quantity, offerInfo.totalPrice, inventory.availableQuantity, offerInfo.pricingDivisor, isAwarded]);

  const isDonation = offerInfo?.destinationType === destinationType.donation;

  const actionInProgress = awardLoading || unawardLoading || updateBidOnAwardedOfferListingLoading;

  const ignoreInProgress = ignoreLoading || restoreLoading;

  const negotiationInProgress = negotiateLoading || unawardAndNegotiateLoading;

  const loadingInProgress = actionInProgress || ignoreInProgress || negotiationInProgress;

  const { outgoingShifts, appliedShift } = useMemo(() => {
    if (offerShiftContextValue?.shiftMap.has(offerInfo._id)) {
      const shifts = offerShiftContextValue.shiftMap.get(offerInfo._id) || [];
      // to-do: get ALL shifts relevant to this offerListing
      return shifts.reduce(
        (shiftTypes, shift) => {
          if (shift.status === OFFER_SHIFT_STATUS.ACCEPTED && shift.newOfferListingId === offerInfo._id) {
            shiftTypes.appliedShift = shift;
          } else {
            shiftTypes.outgoingShifts.push(shift);
          }
          return shiftTypes;
        },
        { outgoingShifts: [], appliedShift: undefined } as { outgoingShifts: OfferCardOfferShift[]; appliedShift?: OfferCardOfferShift },
      );
    }
    return { outgoingShifts: [], appliedShift: undefined };
  }, [offerInfo._id, offerShiftContextValue]);

  const canAward = () => {
    if (quantity > inventory.availableQuantity) return false;
    if (quantity === 0) return false;
    if (!(unitPrice > 0) && !isDonation) return false;
    if (typeof unitPrice !== 'number') return false;
    return true;
  };

  const handleError = (message: string): void => {
    AlertService.alert({ type: 'warning', autoDismiss: true, message: <span>{message}</span>, dismissDelay: 6000 });
  };

  const genericMessageGenerator = (message: string) =>
    `Sorry there was an error ${message} your offer. If this problem persists, please contact a Spoiler Alert Administrator to help you.`;

  const handleAward = Timing.throttle(() => {
    if (loadingInProgress || !canAward()) return;
    if (setActionInProgress) setActionInProgress(true);
    setOfferCardsEnabledState(true);
    awardOfferListing({
      variables: { id: offerInfo._id, quantity, costPerCase: (unitPrice * (offerInfo.pricingDivisor || 1)).toString() },
      update: (cache) => {
        deleteCachedFieldsOnUserQuery(cache, ['awardedOfferListings', 'awardedInventoryFilterParameters']);
        deleteCachedFieldsOnOfferReviewPollingQuery(cache);
      },
      refetchQueries: refetchQueries(),
    })
      .then((res) => {
        if (res.data.awardOfferListing.errors && res.data.awardOfferListing.errors.length > 0) {
          handleError(res.data.awardOfferListing.errors[0].message);
        }
        Tracking.Event(`Offer was awarded ${fullstoryTag || ''}`, {
          userId: user._id,
          offerId: offerInfo._id,
        });
      })
      .catch(() => {
        handleError(
          'We were unable to award this offer due to an unknown error. Our team has been notified and are looking into the issue. Please contact customer support if the issue persists.',
        );
      })
      .finally(() => {
        setOfferCardsEnabledState(false);
        setShowWarningModal(false);
        if (setActionInProgress) setActionInProgress(false);
      });
  }, 1000);

  const handleUnaward = Timing.throttle(() => {
    if (loadingInProgress) return;
    if (setActionInProgress) setActionInProgress(true);
    setQuantity(offerInfo.originalQuantity);
    unawardOfferListing({
      variables: { id: offerInfo._id },
      update: (cache) => deleteCachedFieldsOnUserQuery(cache, ['awardedOfferListings', 'awardedInventoryFilterParameters']),
      refetchQueries: refetchQueries(),
    })
      .then(() => {
        Tracking.Event(`Offer was unawarded ${fullstoryTag || ''}`, {
          userId: user._id,
          offerId: offerInfo._id,
        });
      })
      .catch(() => {
        handleError(genericMessageGenerator('unawarding'));
      })
      .finally(() => {
        if (setActionInProgress) setActionInProgress(false);
      });
  }, 1000);

  const handleIgnore = Timing.throttle(() => {
    if (loadingInProgress) return;
    if (setActionInProgress) setActionInProgress(true);
    ignoreOfferListing({
      variables: { offerListingIds: [offerInfo._id] },
      refetchQueries: refetchQueries(),
    })
      .then((ignoreResp) => {
        if (ignoreResp.data?.ignoreOfferListingsById?.errors?.length) return handleError(ignoreResp.data?.ignoreOfferListingsById?.errors[0].message);
        const trackingData: { [key: string]: string } = {
          userId: user._id,
          offerId: offerInfo._id,
          offerStatus: offerInfo.status,
        };
        if (offerInfo?.negotiation?.status) trackingData.negotiationStatus = offerInfo.negotiation.status;
        return Tracking.Event('Offer was ignored', trackingData);
      })
      .catch(() => handleError(genericMessageGenerator('ignoring')))
      .finally(() => {
        if (setActionInProgress) setActionInProgress(false);
      });
  }, 1000);

  const handleRestore = Timing.throttle(() => {
    if (loadingInProgress) return;
    if (setActionInProgress) setActionInProgress(true);
    restoreOfferListing({
      variables: { offerListingIds: [offerInfo._id] },
      refetchQueries: refetchQueries(),
    })
      .then((restoreResp) => {
        if (restoreResp.data?.restoreOfferListingsById?.errors?.length)
          return handleError(restoreResp.data?.restoreOfferListingsById?.errors[0].message);
        return Tracking.Event('Offer was restored', {
          userId: user._id,
          offerId: offerInfo._id,
        });
      })
      .catch(() => handleError(genericMessageGenerator('restoring')))
      .finally(() => {
        if (setActionInProgress) setActionInProgress(false);
      });
  }, 1000);

  const handleNegotiation = Timing.throttle(() => {
    if (loadingInProgress) return;
    if (setActionInProgress) setActionInProgress(true);
    negotiateOfferListing({
      variables: {
        counterOffers: [{ offerListingId: offerInfo._id, counterOfferAnchor: 'Default' }],
        negotiationOrigin: negotiationOrigin.OFFER_CARD_PRIMARY,
      },
      refetchQueries: refetchQueries(true),
    })
      .then((response) => {
        if (response.data?.createNegotiationStagedListings?.errors?.length)
          return handleError(response.data?.createNegotiationStagedListings?.errors[0].message);
        const messageInfo = `Negotiation started with ${offerInfo.offer.buyerSite.siteName}`;
        AlertService.alert({ type: 'success', message: <span>{messageInfo}</span>, autoDismiss: true, dismissDelay: 5000 });
        return Tracking.Event('OC - Negotiation Staged', {
          userId: user._id,
          offerId: offerInfo._id,
        });
      })
      .catch(() => {
        handleError(genericMessageGenerator('negotiating'));
      })
      .finally(() => {
        if (setActionInProgress) setActionInProgress(false);
      });
  }, 1000);

  const handleUnawardAndNegotiate = Timing.throttle(() => {
    if (loadingInProgress) return;
    if (setActionInProgress) setActionInProgress(true);
    unawardAndNegotiateOfferListing({
      variables: { id: offerInfo._id, negotiationOrigin: negotiationOrigin.OFFER_CARD_PRIMARY },
      refetchQueries: refetchQueries(true),
    })
      .then((response) => {
        if (response.data?.unawardAndNegotiateOfferListing?.errors?.length)
          return handleError(response.data?.unawardAndNegotiateOfferListing?.errors[0].message);
        const messageInfo = `Negotiation started with ${offerInfo.offer.buyerSite.siteName}`;
        AlertService.alert({ type: 'success', message: <span>{messageInfo}</span>, autoDismiss: true, dismissDelay: 5000 });
        return Tracking.Event('Offer was unawarded and negotiated', {
          userId: user._id,
          offerId: offerInfo._id,
          negotiationOrigin: negotiationOrigin.OFFER_CARD_PRIMARY,
          counterOfferAnchor: 'Manual',
        });
      })
      .catch(() => handleError(genericMessageGenerator('unawarding')))
      .finally(() => {
        if (setActionInProgress) setActionInProgress(false);
      });
  }, 1000);

  const handlePostAwardUpdateBid = async (accept: boolean) => {
    try {
      await updateBidOnAwardedOfferListing({
        variables: { id: offerInfo._id, accept: accept },
        refetchQueries: refetchQueries(),
      });
      Tracking.Event('Offer was updated post awarding', { userId: user._id, offerId: offerInfo._id });
    } catch (error) {
      handleError(genericMessageGenerator(`post award bid update error: ${error}`));
    }
  };

  const handleOfferShiftViewClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const contextualShifts = [...outgoingShifts];
    if (appliedShift) contextualShifts.push(appliedShift);
    offerShiftContextValue?.setCurrentShifts(contextualShifts);
  };

  return (
    <OfferCardLayout
      styles={styles}
      disabled={disabled}
      offerInfo={offerInfo}
      inventory={inventory}
      listing={offerInfo.listing}
      buyerSite={offerInfo.offer.buyerSite}
      activeTruckLane={offerInfo.activeTruckLane}
      incomingOfferShift={appliedShift}
      user={user}
      showSuggestions={showSuggestions}
      showNegotiationSuggestions={showNegotiationSuggestions}
      refetchQueries={refetchQueries}
      handleAward={handleAward}
      handleUnaward={handleUnaward}
      handleIgnore={handleIgnore}
      handleRestore={handleRestore}
      handleNegotiation={handleNegotiation}
      handlePostAwardUpdateBid={handlePostAwardUpdateBid}
      handleUnawardAndNegotiate={handleUnawardAndNegotiate}
      showWarningModal={showWarningModal}
      setShowWarningModal={setShowWarningModal}
      quantity={quantity}
      setQuantity={setQuantity}
      unitPrice={unitPrice}
      setUnitPrice={setUnitPrice}
      actionInProgress={actionInProgress}
      ignoreInProgress={ignoreInProgress}
      negotiationInProgress={negotiationInProgress}
      outgoingShifts={outgoingShifts}
      handleOfferShiftViewClick={handleOfferShiftViewClick}
      enabledActions={[
        POSSIBLE_ACTIONS.AWARD,
        POSSIBLE_ACTIONS.IGNORE,
        POSSIBLE_ACTIONS.EDIT_QTY,
        POSSIBLE_ACTIONS.EDIT_PRICE,
        ...(offerInfo.originalOfferListingId ? [] : [POSSIBLE_ACTIONS.NEGOTIATE]),
      ]}
    />
  );
};

export default OfferCardV3;
